import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Badge, Button, Card, CardHeader, Label, Table } from "reactstrap";
import Datetime from "react-datetime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faSearch } from "@fortawesome/free-solid-svg-icons";
import { findCondominiumMovements } from "../../../helpers/api/condominium";
import useSWRFetchPagination from "../../../helpers/hooks/useSWRFetchPagination";
import FullPagination from "../../common/pagination/FullPagination";
import Loader from "../../common/Loader";
import { CurrencyContext } from "../../../context/Context";
import Money, { ConvertedMoney } from "./Money";
import MovementVoucherLink from "../../common/MovementVoucherLink";

const formatDate = (date) => {
  const d = new Date(date);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

// Algunas descripciones llegan como JSON ({ message }) y otras como texto.
const describe = (movement) => {
  try {
    const parsed = JSON.parse(movement.description);
    return parsed?.message || movement.description;
  } catch (error) {
    return movement.description;
  }
};

const counterpartOf = (movement) => {
  if (movement?.property) {
    const user = movement.property.user?.data;
    return `${user?.firstname || ""} ${user?.lastname || ""}`.trim() || "N/A";
  }
  return movement?.provider || "N/A";
};

// Tabla de movimientos del condominio con filtro por fechas. Antes vivía a
// mitad de la página de Finanzas, entre el resumen y los accesos.
const CondominiumMovements = ({ condominiumId, role }) => {
  const { currency: curr } = useContext(CurrencyContext);
  const [range, setRange] = useState({ since: null, to: null });
  const [params, setParams] = useState({
    condominiumId: role === "manager" ? condominiumId : null,
    start_date: null,
    end_date: null,
  });

  const {
    data,
    isLoading,
    handler: { goToPage, nextPage, perPage, previousPage },
    meta,
  } = useSWRFetchPagination(findCondominiumMovements, params);

  // La tasa guardada en el movimiento manda; si no la trae, la del día.
  const rateOf = (movement) => {
    const stored = Number(
      movement?.exchange_rate?.value ?? movement?.exchange_rate ?? movement?.rate ?? 0
    );
    return stored > 0 ? stored : Number(curr?.value || 1);
  };

  const search = () =>
    setParams((prev) => ({
      ...prev,
      start_date: range.since.format("YYYY-MM-DD"),
      end_date: range.to.format("YYYY-MM-DD"),
    }));

  const movements = data || [];

  return (
    <Card>
      <CardHeader className="d-flex flex-wrap justify-content-between align-items-center">
        <h5 className="mb-2 mb-lg-0 mr-3">Movimientos del condominio</h5>
        <div className="d-flex flex-wrap align-items-center">
          <Label className="mb-0 mr-2 fs--1">Desde</Label>
          <Datetime
            className="mr-2"
            value={range.since}
            isValidDate={(current) => !current.isAfter()}
            onChange={(since) => setRange((prev) => ({ ...prev, since }))}
            dateFormat="DD/MM/YYYY"
            timeFormat={false}
          />
          <Label className="mb-0 mr-2 fs--1">Hasta</Label>
          <Datetime
            className="mr-2"
            value={range.to}
            isValidDate={(current) => current.isAfter(range.since) && !current.isAfter()}
            onChange={(to) => setRange((prev) => ({ ...prev, to }))}
            dateFormat="DD/MM/YYYY"
            timeFormat={false}
          />
          <Button
            color="falcon-default"
            size="sm"
            disabled={!range.since || !range.to}
            onClick={search}
          >
            <FontAwesomeIcon icon={faSearch} className="mr-1" />
            Buscar
          </Button>
        </div>
      </CardHeader>
      {isLoading ? (
        <Loader />
      ) : movements.length > 0 ? (
        <>
          <Table responsive className="fs--1 mb-0">
            <thead className="bg-200 text-900">
              <tr>
                <th>Fecha</th>
                <th>Propietario o proveedor</th>
                <th>Descripción</th>
                <th>Tipo</th>
                <th className="text-right">Monto</th>
              </tr>
            </thead>
            <tbody>
              {movements.map((movement) => {
                const isIncome = movement.isDecrement === 0;
                return (
                  <tr key={movement.id}>
                    <td className="text-nowrap">{formatDate(movement.created_at)}</td>
                    <td>{counterpartOf(movement)}</td>
                    <td>
                      {describe(movement)}
                      <MovementVoucherLink voucher={movement.voucher} />
                    </td>
                    <td>
                      <Badge
                        pill
                        className={isIncome ? "badge-soft-success" : "badge-soft-warning"}
                      >
                        {isIncome ? "Ingreso" : "Egreso"}
                        <FontAwesomeIcon
                          icon={isIncome ? faArrowUp : faArrowDown}
                          className="ml-1"
                        />
                      </Badge>
                    </td>
                    <td className="text-right text-nowrap">
                      <Money value={movement.amount} className="d-block font-weight-semi-bold" />
                      <ConvertedMoney
                        converted={
                          movement.amount_converted ||
                          Number(movement.amount || 0) * rateOf(movement)
                        }
                        className="d-block text-600"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <FullPagination
            nextPage={nextPage}
            prevPage={previousPage}
            goToPage={goToPage}
            meta={meta}
            className="shadow-none"
            posText="Movimientos"
            perPage={perPage}
          />
        </>
      ) : (
        <Alert color="info" className="m-3">
          Todavía no hay movimientos para mostrar.
        </Alert>
      )}
    </Card>
  );
};

CondominiumMovements.propTypes = {
  condominiumId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  role: PropTypes.string,
};

export default CondominiumMovements;
